/**
 * DistributionPanel — 超级管理员放号管理面板
 * 功能：开始/停止放号、显示链接、实时计数、历史记录
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Play, Square, Copy, Check, Loader2, Radio, History, Users, ExternalLink, QrCode, Download } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import api from '../api';
import AlertToast from './AlertToast';
import { copyTextToClipboard } from '../utils/browserCompat'; // http 站点没有 navigator.clipboard

export default function DistributionPanel({ open, onClose }) {
  const [session, setSession] = useState(null);      // 当前活跃会话
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({ count: 0, registered: 0 });
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const [durationDays, setDurationDays] = useState('');
  const [membershipLevel, setMembershipLevel] = useState('education');  // 放号版本
  const [showQr, setShowQr] = useState(false);   // 二维码展开/收起
  const qrWrapRef = useRef(null);                 // 二维码容器（用于导出图片）
  const pollRef = useRef(null);

  useEffect(() => {
    if (toast.message) {
      const t = setTimeout(() => setToast({ message: '', type: 'success' }), 2500);
      return () => clearTimeout(t);
    }
  }, [toast.message]);

  // 加载当前会话
  const loadSession = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/distribution/session');
      setSession(data.session);
      if (data.session) {
        setStats({ count: data.session.claim_count || 0, registered: 0 });
      }
    } catch (err) {
      console.error('加载放号会话失败:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 轮询统计
  const pollStats = useCallback(async () => {
    try {
      const data = await api.get('/api/distribution/stats');
      if (data.active) {
        setStats({ count: data.count, registered: data.registered });
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (open) {
      loadSession();
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [open, loadSession]);

  // 活跃会话时每 3 秒轮询
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (session && session.status === 'active') {
      pollRef.current = setInterval(pollStats, 3000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [session, pollStats]);

  // 开始放号
  const handleStart = async () => {
    setStarting(true);
    try {
      const body = {};
      if (durationDays && parseInt(durationDays, 10) > 0) body.duration_days = parseInt(durationDays, 10);
      body.membership_level = membershipLevel;
      const data = await api.post('/api/distribution/start', body);
      if (data.success) {
        setSession(data.session);
        setStats({ count: 0, registered: 0 });
        setToast({ message: '放号已开始！', type: 'success' });
      }
    } catch (err) {
      setToast({ message: err.message || '开始放号失败', type: 'warning' });
    } finally {
      setStarting(false);
    }
  };

  // 停止放号
  const handleStop = async () => {
    if (!window.confirm('确定要停止放号吗？新设备将无法领取邀请码。')) return;
    setStopping(true);
    try {
      const data = await api.post('/api/distribution/stop');
      if (data.success) {
        setSession(null);
        setToast({ message: '放号已停止', type: 'success' });
      }
    } catch (err) {
      setToast({ message: err.message || '停止失败', type: 'warning' });
    } finally {
      setStopping(false);
    }
  };

  // 加载历史
  const loadHistory = async () => {
    try {
      const data = await api.get('/api/distribution/history');
      setHistory(data);
    } catch { /* ignore */ }
  };

  const toggleHistory = () => {
    if (!showHistory) loadHistory();
    setShowHistory(v => !v);
  };

  // 复制链接
  const getLink = () => {
    if (!session) return '';
    const origin = window.location.origin;
    return `${origin}/invite/${session.link_token}`;
  };

  const handleCopy = async () => {
    if (await copyTextToClipboard(getLink())) {
      setCopied(true);
      setToast({ message: '链接已复制', type: 'success' });
      setTimeout(() => setCopied(false), 2000);
    } else {
      setToast({ message: '复制失败', type: 'warning' });
    }
  };

  // 下载二维码为 PNG 图片
  const handleDownloadQr = () => {
    const canvas = qrWrapRef.current?.querySelector('canvas');
    if (!canvas) {
      setToast({ message: '二维码尚未生成', type: 'warning' });
      return;
    }
    try {
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `放号二维码_${session?.link_token || 'qr'}.png`;
      a.click();
      setToast({ message: '二维码已下载', type: 'success' });
    } catch {
      setToast({ message: '下载失败', type: 'warning' });
    }
  };

  const formatDate = (str) => {
    if (!str) return '-';
    return new Date(str).toLocaleString('zh-CN', {
      month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  };

  if (!open) return null;

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        {/* Header */}
        <div style={S.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={S.iconBadge}>
              <Radio size={20} color="#fff" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#fff' }}>放号管理</h2>
              <p style={{ margin: 0, fontSize: 12, color: '#71717a' }}>
                {session ? '正在放号中' : '未开始放号'}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={S.closeBtn}><X size={20} /></button>
        </div>

        {/* Body */}
        <div style={S.body}>
          {loading ? (
            <div style={S.center}>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#8b5cf6' }} />
            </div>
          ) : session ? (
            /* ── 活跃状态 ── */
            <>
              {/* 实时统计卡片 */}
              <div style={S.statsCard}>
                <div style={S.statItem}>
                  <div style={S.statNumber}>{stats.count}</div>
                  <div style={S.statLabel}>已放出</div>
                </div>
                <div style={S.statDivider} />
                <div style={S.statItem}>
                  <div style={S.statNumber}>{stats.registered}</div>
                  <div style={S.statLabel}>已注册</div>
                </div>
                <div style={S.liveIndicator}>
                  <span style={S.liveDot} />
                  <span style={{ fontSize: 11, color: '#4ade80' }}>实时</span>
                </div>
              </div>

              {/* 链接 */}
              <div style={S.linkSection}>
                <label style={S.label}>放号链接</label>
                <div style={S.linkRow}>
                  <input readOnly value={getLink()} style={S.linkInput} onClick={e => e.target.select()} />
                  <button onClick={handleCopy} style={S.copyBtn}>
                    {copied ? <Check size={16} color="#4ade80" /> : <Copy size={16} />}
                  </button>
                  <button onClick={() => window.open(getLink(), '_blank')} style={S.copyBtn} title="预览">
                    <ExternalLink size={16} />
                  </button>
                  <button
                    onClick={() => setShowQr(v => !v)}
                    style={{ ...S.copyBtn, ...(showQr ? S.copyBtnActive : {}) }}
                    title="二维码"
                  >
                    <QrCode size={16} />
                  </button>
                </div>

                {/* 二维码展示区 */}
                {showQr && (
                  <div style={S.qrSection}>
                    <div ref={qrWrapRef} style={S.qrCanvasWrap}>
                      <QRCodeCanvas
                        value={getLink()}
                        size={180}
                        level="M"
                        marginSize={2}
                        bgColor="#ffffff"
                        fgColor="#000000"
                      />
                    </div>
                    <p style={S.qrHint}>扫码直达放号链接</p>
                    <button onClick={handleDownloadQr} style={S.qrDownloadBtn}>
                      <Download size={14} />
                      <span>下载二维码</span>
                    </button>
                  </div>
                )}
              </div>

              {/* 停止按钮 */}
              <button onClick={handleStop} disabled={stopping} style={S.stopBtn}>
                {stopping ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Square size={16} />}
                <span>{stopping ? '停止中…' : '停止放号'}</span>
              </button>
            </>
          ) : (
            /* ── 未开始状态 ── */
            <>
              <div style={S.emptyState}>
                <Radio size={48} style={{ opacity: 0.2, color: '#8b5cf6' }} />
                <p style={{ color: '#71717a', fontSize: 14, margin: '12px 0 4px' }}>
                  点击下方按钮开始放号
                </p>
                <p style={{ color: '#52525b', fontSize: 12, margin: 0 }}>
                  系统将生成唯一链接，不同设备访问获得不同邀请码
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 10 }}>
                <span style={{ color: '#94a3b8', fontSize: 13 }}>放号版本</span>
                {[
                  { value: 'education', label: '教育版' },
                  { value: 'commercial', label: '商用版' },
                  { value: 'vip', label: 'VIP版' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setMembershipLevel(opt.value);
                      // 教育版默认永久（清空天数）；商用/VIP 需手动填天数
                      if (opt.value === 'education') setDurationDays('');
                    }}
                    style={{
                      padding: '7px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                      background: membershipLevel === opt.value ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${membershipLevel === opt.value ? 'rgba(139, 92, 246, 0.5)' : 'rgba(255,255,255,0.1)'}`,
                      color: membershipLevel === opt.value ? '#a78bfa' : '#94a3b8',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 8 }}>
                <span style={{ color: '#94a3b8', fontSize: 13 }}>账户有效期</span>
                <input
                  type="number" min="0" placeholder="永久"
                  value={durationDays}
                  onChange={e => setDurationDays(e.target.value)}
                  style={{
                    width: 70, padding: '8px 10px', borderRadius: 8, textAlign: 'center',
                    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                    color: '#fff', fontSize: 13, outline: 'none'
                  }}
                />
                <span style={{ color: '#71717a', fontSize: 12 }}>天（留空=永久）</span>
              </div>
              <button onClick={handleStart} disabled={starting} style={S.startBtn}>
                {starting ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={16} />}
                <span>{starting ? '正在启动…' : '开始放号'}</span>
              </button>
            </>
          )}

          {/* 历史记录 */}
          <button onClick={toggleHistory} style={S.historyToggle}>
            <History size={14} />
            <span>{showHistory ? '收起历史' : '查看历史'}</span>
          </button>

          {showHistory && (
            <div style={S.historyList}>
              {history.length === 0 ? (
                <p style={{ color: '#52525b', fontSize: 13, textAlign: 'center', margin: 0 }}>暂无历史记录</p>
              ) : history.map(h => (
                <div key={h.id} style={S.historyItem}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 13, color: h.status === 'active' ? '#4ade80' : '#71717a' }}>
                      {h.status === 'active' ? '●  进行中' : '■  已结束'}
                    </span>
                    <span style={{ fontSize: 12, color: '#52525b', marginLeft: 8 }}>
                      {formatDate(h.created_at)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Users size={12} color="#71717a" />
                    <span style={{ fontSize: 13, color: '#a1a1aa' }}>{h.claim_count}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <AlertToast message={toast.message} type={toast.type} />
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const S = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.8)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  modal: {
    width: 440, maxHeight: '80vh', background: '#1a1a1d',
    borderRadius: 16, overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.1)',
    display: 'flex', flexDirection: 'column',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.1)',
    flexShrink: 0,
  },
  iconBadge: {
    width: 36, height: 36, borderRadius: 10,
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  closeBtn: {
    background: 'transparent', border: 'none', padding: 8, cursor: 'pointer', color: '#666',
  },
  body: {
    padding: '20px 24px 24px', overflowY: 'auto', flex: 1,
    display: 'flex', flexDirection: 'column', gap: 16,
  },
  center: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40,
  },
  // 统计卡片
  statsCard: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(34, 197, 94, 0.06)',
    border: '1px solid rgba(34, 197, 94, 0.2)',
    borderRadius: 16, padding: '20px 32px',
    position: 'relative',
  },
  statItem: { textAlign: 'center', flex: 1 },
  statNumber: { fontSize: 32, fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' },
  statLabel: { fontSize: 12, color: '#71717a', marginTop: 4 },
  statDivider: { width: 1, height: 40, background: 'rgba(255,255,255,0.1)', margin: '0 24px' },
  liveIndicator: {
    position: 'absolute', top: 12, right: 16,
    display: 'flex', alignItems: 'center', gap: 4,
  },
  liveDot: {
    width: 6, height: 6, borderRadius: '50%', background: '#4ade80',
    animation: 'pulse 2s infinite',
  },
  // 链接
  linkSection: {},
  label: { fontSize: 12, color: '#71717a', marginBottom: 6, display: 'block' },
  linkRow: {
    display: 'flex', gap: 6,
  },
  linkInput: {
    flex: 1, padding: '10px 12px', borderRadius: 8,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#a1a1aa', fontSize: 13, outline: 'none',
    fontFamily: 'Monaco, Consolas, monospace',
  },
  copyBtn: {
    padding: '10px 12px', borderRadius: 8,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#94a3b8', cursor: 'pointer',
    display: 'flex', alignItems: 'center',
  },
  copyBtnActive: {
    background: 'rgba(139, 92, 246, 0.2)',
    border: '1px solid rgba(139, 92, 246, 0.5)',
    color: '#a78bfa',
  },
  // 二维码
  qrSection: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
    marginTop: 12, padding: '16px',
    borderRadius: 12, background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  qrCanvasWrap: {
    padding: 10, borderRadius: 10, background: '#fff',
    lineHeight: 0,
  },
  qrHint: { margin: 0, fontSize: 12, color: '#71717a' },
  qrDownloadBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '8px 16px', borderRadius: 8,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#a1a1aa', fontSize: 13, cursor: 'pointer',
  },
  // 按钮
  startBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '14px', borderRadius: 12,
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    border: 'none', color: '#fff', fontSize: 15, fontWeight: 500,
    cursor: 'pointer',
  },
  stopBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '14px', borderRadius: 12,
    background: 'rgba(239, 68, 68, 0.15)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    color: '#ef4444', fontSize: 15, fontWeight: 500,
    cursor: 'pointer',
  },
  emptyState: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0',
  },
  // 历史
  historyToggle: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '8px', borderRadius: 8,
    background: 'transparent', border: 'none',
    color: '#71717a', fontSize: 13, cursor: 'pointer',
  },
  historyList: {
    display: 'flex', flexDirection: 'column', gap: 6,
  },
  historyItem: {
    display: 'flex', alignItems: 'center', padding: '10px 12px',
    borderRadius: 8, background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.05)',
  },
};
